/**
 * Cloudflare Stream helpers — single source of truth for the customer subdomain
 * and HLS manifest URL. Mirrors iOS `CloudflarePlayer.swift` /
 * `CloudflareVideoPlayer.tsx`. Centralizing the constant + URL template here
 * keeps the subdomain from drifting between the feed player, the lessons-track
 * player, and the flight school activities.
 */
const CLOUDFLARE_CUSTOMER_SUBDOMAIN = "customer-p3osu754g3aixqn3";

/** Cloudflare Stream HLS (.m3u8) manifest URL for a given Stream video id. */
export function cloudflareHlsUrl(cloudflareId: string): string {
  return `https://${CLOUDFLARE_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${cloudflareId}/manifest/video.m3u8`;
}

/**
 * Cloudflare Stream thumbnail (poster) URL for a given Stream video id, sampled
 * at `startMs` into the clip. Mirrors iOS `CloudflarePlayer.swift` setupThumbnail
 * (`/thumbnails/thumbnail.jpg?time={startSeconds}s`, startSeconds = Int(start)),
 * so the lessons / Flight School player can paint the first frame BEHIND the
 * video and avoid the black flash before playback begins.
 *
 * `startMs` is the clip's segment start in milliseconds (VideoActivity.start);
 * Cloudflare's `time` param is in seconds, so we floor to whole seconds to match
 * iOS `Int(startSeconds)`.
 */
export function cloudflareThumbnailUrl(
  cloudflareId: string,
  startMs = 0,
): string {
  const startSeconds = Math.max(0, Math.floor(startMs / 1000));
  return `https://${CLOUDFLARE_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${cloudflareId}/thumbnails/thumbnail.jpg?time=${startSeconds}s`;
}

/**
 * ReactPlayer `config.file` block for Cloudflare HLS clips. react-player defaults
 * to hls.js 1.1.4 (2021), whose VOD deep-seek is extremely slow — a clip that
 * starts deep into a shared source (e.g. Flight School / review-card clips) takes
 * minutes to land. Pin a current hls.js (fast seeks) and begin loading AT the
 * clip offset so it fetches the clip's segment first instead of buffering the
 * lead-in from 0 (mirrors iOS, which seeks the native player instantly). Shared
 * by every ReactPlayer that plays a Cloudflare clip (feed, lessons/FS, review
 * cards, lesson activities). 0-offset clips use -1 (auto). `startMs` is the
 * clip's segment start in milliseconds.
 */
export function cloudflareFileConfig(startMs = 0) {
  return {
    hlsVersion: "1.5.18",
    hlsOptions: {
      startPosition: startMs > 0 ? startMs / 1000 : -1,
    },
  };
}
